import * as React from "react";
import { cn } from "@/lib/cn";
import { useTranslation } from "react-i18next";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input({ label, hint, error, className, id, type, ...rest }, ref) {
    const { t } = useTranslation();
    const inputId = id ?? React.useId();
    // Password fields get a reveal toggle for free — callers pass
    // type="password" and the eye button appears in the right slot.
    const isPassword = type === "password";
    const [reveal, setReveal] = React.useState(false);
    const effectiveType = isPassword && reveal ? "text" : type;
    const input = (
      <input
        id={inputId}
        ref={ref}
        type={effectiveType}
        className={cn(
          "h-10 w-full rounded-md border border-line-1 bg-bg-2 px-3 text-sm text-t-1 placeholder:text-t-4",
          "transition-colors focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15",
          error &&
            "border-no/60 focus:border-no focus:ring-no/20",
          isPassword && "pr-11",
          className,
        )}
        {...rest}
      />
    );
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-2xs font-semibold uppercase tracking-wide text-t-3"
          >
            {label}
          </label>
        )}
        {isPassword ? (
          <div className="relative">
            {input}
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              tabIndex={-1}
              disabled={rest.disabled}
              aria-label={reveal ? t("common.hidePassword") : t("common.showPassword")}
              aria-pressed={reveal}
              className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-t-3 transition-colors hover:text-t-1 focus:outline-none focus:ring-2 focus:ring-neon/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {reveal ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        ) : (
          input
        )}
        {(error || hint) && (
          <p className={cn("text-2xs", error ? "text-no" : "text-t-3")}>
            {error ?? hint}
          </p>
        )}
      </div>
    );
  },
);

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6 0-10-7-10-7a18.5 18.5 0 0 1 5.16-5.94" />
      <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c6 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}
